<script setup lang="ts">
import { liveStreamService } from '@/api/services/live-stream-service'
import BaseLargeButton from '@/components/base/BaseLargeButton.vue'
import BaseModal from '@/components/base/BaseModal.vue'
import BaseSmallButton from '@/components/base/BaseSmallButton.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import { useModal } from '@/composables/useModal'
import { useToast } from '@/composables/useToast'
import { requestAuthentication } from '@/sdk/bootpay-sdk'
import { useAuthStore } from '@/stores/auth'
import type { LiveStream } from '@/types/live-stream.types'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps<{
  item: LiveStream
}>()

const router = useRouter()
const isMobile = useIsMobile()
const { isOpen, open, close } = useModal()
const authStore = useAuthStore()
const { showToast } = useToast()
const notification = ref(props.item.notification)

const isLive = computed(() => {
  return props.item.startAt < Date.now()
})

// 날짜 포맷 함수
const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp)
  const year = date.getFullYear().toString().slice(2)
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')

  return `${year}.${month}.${day} ${hours}:${minutes} 오픈 예정`
}

const navigateToLiveStreaming = (id: number) => {
  router.push(`/live-streaming/${id}`)
}

const showToggleNotificationModal = async () => {
  if (!authStore.state.account) {
    alert('로그인 후 이용해주세요.')
    return
  }

  if (!authStore.state.account?.authDone) {
    open()
    return
  }

  toggleNotification()
}

const toggleNotification = async () => {
  const message = notification.value ? '알림신청 취소' : '알림신청'

  try {
    const tempNotification = await liveStreamService.toggleNotification(props.item.idx)

    notification.value = tempNotification
    showToast(message + ' 완료')
  } catch {
    showToast(message + ' 실패', 'error')
  }
}

const showVerifyModal = async () => {
  const isSuccess = await requestAuthentication()

  if (isSuccess) {
    toggleNotification()
  }
}
</script>

<template>
  <div class="live-streaming-list-item">
    <div class="live-streaming-list-container">
      <div class="live-streaming-list-item-thumbnail">
        <img :src="props.item.thumbnailUrl" alt="라이브 썸네일" />
      </div>
      <div class="live-streaming-list-item-content">
        <div class="live-streaming-list-item-info">
          <div
            class="live-streaming-list-item-info-started-at title-sm tint-500"
            :class="{ live: isLive, 'body-lg': isMobile }"
          >
            {{ isLive ? '라이브 진행중' : formatDate(props.item.startAt) }}
          </div>
          <div class="title-md white" :class="{ 'title-sm': isMobile }">{{ props.item.title }}</div>
        </div>
        <template v-if="!isMobile">
          <BaseSmallButton
            @click="navigateToLiveStreaming(props.item.idx)"
            v-if="isLive"
            text="라이브 보러가기"
            type="main"
          />
          <BaseSmallButton
            v-else
            :text="notification ? '알림신청 취소' : '알림신청'"
            type="sub"
            @click="showToggleNotificationModal"
          />
        </template>
        <template v-else>
          <BaseLargeButton
            @click="navigateToLiveStreaming(props.item.idx)"
            v-if="isLive"
            text="라이브 보러가기"
            type="main"
          />
          <BaseLargeButton
            v-else
            :text="notification ? '알림신청 취소' : '알림신청'"
            type="sub"
            @click="showToggleNotificationModal"
          />
        </template>
      </div>
    </div>
  </div>

  <BaseModal
    title="알림신청"
    :is-open="isOpen"
    confirm-text="확인"
    cancel-text="취소"
    @confirm="showVerifyModal"
    @close="close"
  >
    <div class="notification-modal-content body-sm gray-200">
      알림톡을 받기 위해 본인인증을 진행해 주세요.
    </div>
  </BaseModal>
</template>

<style scoped>
.live-streaming-list-item {
  width: 100%;
  position: relative;
}

.live-streaming-list-container {
  display: flex;
  flex-direction: row;
  background-color: transparent;
  border-radius: 8px;
  overflow: hidden;
}

.live-streaming-list-item-thumbnail {
  position: relative;
  width: 847px;
  height: 477px;
  flex-shrink: 0;
}

.live-streaming-list-item-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.live-streaming-list-item-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: var(--gray-800);
  padding: 20px;
  border-radius: 8px;
  margin-left: 20px;
  flex-grow: 1;
}

.live-streaming-list-item-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.button-join {
  width: 100%;
  padding: 12px;
  background-color: #ff4b4b;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 20px;
}

.button-join:hover {
  background-color: #e03a3a;
}

.notification-modal-content {
  margin-bottom: 28px;
}

@media (max-width: 1365px) {
  .live-streaming-list-container {
    flex-direction: column;
    border-radius: 0px;
  }

  .live-streaming-list-item-thumbnail {
    width: 100%;
    height: auto;
    aspect-ratio: 375/210;
  }

  .live-streaming-list-item-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0px;
  }

  .live-streaming-list-item-content {
    margin-left: 0;
    border-radius: 0px;
    padding: 12px 20px;
    background-color: transparent;
  }

  .live-streaming-list-item-info {
    gap: 12px;
    margin-bottom: 20px;
  }
}
</style>
